/** Shared building blocks for the settings sections. */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Card } from '@/components/ui/Card';
import { TextField } from '@/components/ui/TextField';
import { toNumber } from '@/lib/format';
import type { MerchantSettings } from '@/types';

export interface SettingsSectionProps {
  settings: MerchantSettings;
  onChange: (patch: Partial<MerchantSettings>) => void;
}

export function SettingsCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <h2 className="text-base font-semibold text-ink">{title}</h2>
      {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </Card>
  );
}

/** Numeric field that stores a number (not a string) on change. */
export function NumberField({
  label,
  value,
  onChange,
  hint,
  suffix,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  hint?: string;
  suffix?: string;
}) {
  return (
    <TextField
      label={suffix ? `${label} (${suffix})` : label}
      type="number"
      inputMode="decimal"
      hint={hint}
      value={value === undefined ? '' : String(value)}
      onChange={(event) => onChange(toNumber(event.target.value))}
    />
  );
}

/**
 * Row-based settings card: a titled header followed by divider-separated rows.
 * Each child should be a <PolicyRow>.
 */
export function PolicyCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-line px-5 py-4 sm:px-6">
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      </div>
      <div className="divide-y divide-line">{children}</div>
    </Card>
  );
}

/** A single labelled row inside a <PolicyCard>: label/description left, control right. */
export function PolicyRow({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 px-5 py-5 sm:grid-cols-[1fr_minmax(0,22rem)] sm:gap-8 sm:px-6">
      <div>
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

/** Chip/tag editor that maps to/from a string[]. Type + Enter to add, × to remove. */
export function TagInput({
  values,
  onChange,
  placeholder = 'Add values (comma-separated)',
  transform,
}: {
  values: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Optional label transform for display only (stored value is unchanged). */
  transform?: (value: string) => string;
}) {
  const [text, setText] = useState('');

  function commit() {
    const entries = text
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry && !values.includes(entry));
    if (entries.length > 0) {
      onChange([...values, ...entries]);
    }
    setText('');
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-2 focus-within:ring-2 focus-within:ring-brand">
      {values.map((value) => (
        <span
          key={value}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.06] py-1 pl-3 pr-2 text-sm text-ink"
        >
          {transform ? transform(value) : value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            onClick={() => onChange(values.filter((entry) => entry !== value))}
            className="text-ink-subtle transition-colors hover:text-ink"
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Backspace' && !text && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={values.length ? '' : placeholder}
        className="min-w-[8rem] flex-1 bg-transparent px-1 py-1 text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
      />
    </div>
  );
}

/** Comma-separated list editor that maps to/from a string[]. */
export function CsvField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  hint?: string;
}) {
  return (
    <TextField
      label={label}
      hint={hint ?? 'Comma-separated'}
      value={(value ?? []).join(', ')}
      onChange={(event) =>
        onChange(
          event.target.value
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean),
        )
      }
    />
  );
}
